import React, { useEffect, useMemo, useState } from "react";
import { useHistory, useParams } from "react-router";
import {
  Box,
  Button,
  Chip,
  Modal,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  IconButton,
  CircularProgress,
} from "@mui/material";
import {
  CaretRight,
  CheckCircle,
  MagnifyingGlass,
  PencilSimple,
  Plus,
  Trash,
  X,
} from "phosphor-react";
import AddOrEditWebhookModal from "./AddOrEditWebhookModal";
import useStyles from "./Webhook.styles";
import { fdelete, fget } from "../../../../API/callsAPI";
import DateAndTimeViewer from "../../../Utilities/DateAndTimeViewer/DateAndTimeViewer.component";
import ChitranSvg from "../../../Utilities/Svg/ChitranSvg";
import DeleteModal from "../../../Utilities/DeleteModal/DeleteModal.component";

const statusOptions = ["All Statuses", "Active", "Inactive"];

const WebhookItemsTable = ({ projectId, setSnackValue }) => {
  const classes = useStyles();
  const history = useHistory();
  const params = useParams();
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("All Statuses");
  const [webhook, setWebhook] = useState("");
  const [webhookLogs, setWebhookLogs] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [eventsList, setEventsList] = useState([]);
  const [addModalOpen, setAddModalOpen] = useState({
    open: false,
    data: "",
  });
  const [openDeleteModal, setOpenDeleteModal] = useState({
    open: false,
    data: {},
  });
  const [openPreviewModal, setOpenPreviewModal] = useState({
    open: false,
    data: {},
  });

  async function getWebhooksEvents() {
    try {
      const res = await fget({
        url: "/webhooks/events/",
      });
      if (res.status === 200) {
        setEventsList(res.data);
      }
    } catch (error) {
      console.log(error);
    }
  }

  const getWebhookLogs = async () => {
    setIsLoading(true);
    try {
      const res = await fget({
        url: `webhooks/deliveries/?webhook=${params.id}`,
      });
      if (res.status === 200) {
        setWebhookLogs(res.data);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  async function getWebhook() {
    try {
      const res = await fget({
        url: `/webhooks/?project=${projectId}&webhook=${params.id}`,
      });
      if (res.status === 200) {
        const webhook = res.data.results.filter(
          (webhook) => webhook.id == params.id,
        );
        if (webhook.length > 0) {
          setWebhook(webhook[0]);
        }
      }
    } catch (error) {
      console.log(error);
    }
  }

  async function removeWebhook(id) {
    try {
      const res = await fdelete({
        url: `/webhooks/${id}/`,
      });
      if (res.status === 204) {
        setOpenDeleteModal({
          open: false,
          data: {},
        });
        getWebhook();
      }
    } catch (error) {
      console.log(error);
    }
  }

  useEffect(() => {
    if (params.id) {
      getWebhookLogs();
      if (projectId) {
        getWebhook();
      }
    }
    getWebhooksEvents();
  }, [params.id, projectId]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "7px",
        width: "100%",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "12px",
            flexWrap: "wrap",
          }}
        >
          <Typography
            sx={{
              fontSize: "20px",
              fontWeight: 500,
              color: "#606479",
              cursor: "pointer",
              "&: hover": {
                color: "#f9f9f9",
              },
            }}
            onClick={() => {
              history.push(`/${projectId}/settings/webhooks`);
            }}
          >
            Webhooks
          </Typography>
          <IconButton>
            <CaretRight size={14} color="#606479" />
          </IconButton>
          <Typography
            sx={{ fontSize: "20px", fontWeight: 500, color: "#F9F9F9" }}
          >
            {webhook && webhook.name}
          </Typography>
        </Box>
        {/* <Typography sx={{ color: "#B1B5C9", fontSize: "12px" }}>
          Created 3d ago by Siddesh Sharma
        </Typography> */}
      </Box>

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            flexWrap: "wrap",
          }}
        >
          <CheckCircle size={20} color="#00FF444D" weight="fill" />
          <Typography color="#ECECEC" fontSize={14}>
            {webhook && webhook.url}
          </Typography>
        </Box>

        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          <Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            size="small"
            sx={{
              minWidth: 180,
              bgcolor: "#252A38",
              color: "#F9F9F9",
              borderRadius: "10px",
              ".MuiOutlinedInput-notchedOutline": { border: "none" },
              //the arrow color - #606479
            }}
            MenuProps={{
              PaperProps: {
                style: {
                  backgroundColor: "#252A38",
                  color: "#fff !important",
                  fontFamily: "Poppins, sans-serif !important",
                },
                "& .MuiMenuItem-root": {
                  color: "#fff !important",
                  backgroundColor: "#252A38",
                  fontFamily: "Poppins, sans-serif !important",
                },
              },
            }}
          >
            {statusOptions.map((option) => (
              <MenuItem key={option} value={option} sx={{ color: "#F9F9F9" }}>
                {option}
              </MenuItem>
            ))}
          </Select>
          <Button
            variant="outlined"
            sx={{
              textTransform: "none",
              borderRadius: "10px",
              border: "2px solid #6C65D9",
              color: "#F9F9F9",
              minWidth: 135,
            }}
            onClick={(e) => {
              e.stopPropagation();
              setAddModalOpen({
                open: true,
                data: webhook,
              });
            }}
          >
            Edit Webhook
          </Button>
          <Button
            variant="outlined"
            sx={{
              textTransform: "none",
              borderRadius: "10px",
              border: "2px solid #D61F1F",
              minWidth: 140,
              color: "#F9F9F9",
            }}
            onClick={(e) => {
              e.stopPropagation();
              setOpenDeleteModal({
                open: true,
                data: webhook,
              });
            }}
          >
            Delete Webhook
          </Button>
        </Box>
      </Box>
      {isLoading && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "100%",
            height: "100%",
            marginTop: "200px",
          }}
        >
          <CircularProgress />
        </div>
      )}
      {!isLoading && webhookLogs.count === 0 && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexDirection: "column",
            width: "100%",
            height: "100%",
            gap: "18px",
            marginTop: "24px",
          }}
        >
          <ChitranSvg />
          <Typography>This webhook has no activity yet</Typography>
        </div>
      )}
      {!isLoading && webhookLogs.count > 0 && (
        <TableContainer>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell className={classes.tableHeader} align="left">
                  Log ID
                </TableCell>
                <TableCell className={classes.tableHeader} align="center">
                  Event Type
                </TableCell>
                <TableCell align="center" className={classes.tableHeader}>
                  Timestamp
                </TableCell>
                <TableCell align="center" className={classes.tableHeader}>
                  Status
                </TableCell>
                <TableCell align="center" className={classes.tableHeader}>
                  Response Code
                </TableCell>
                <TableCell align="center" className={classes.tableHeader}>
                  Response Time (ms)
                </TableCell>
                <TableCell align="right" className={classes.tableHeader}>
                  Payload
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {webhookLogs.results.map((webhookLog) => {
                return (
                  <TableRow
                    key={webhookLog.id}
                    sx={{
                      "&:hover": { backgroundColor: "rgba(255,255,255,0.04)" },
                    }}
                  >
                    <TableCell
                      align="left"
                      sx={{
                        color: "#F9F9F9",
                        fontSize: "14px",
                        maxWidth: "340px",
                        wordBreak: "break-all",
                        borderColor: "#606479",
                      }}
                    >
                      {webhookLog.activity_log}
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        color: "#F9F9F9",
                        fontSize: "14px",
                        borderColor: "#606479",
                      }}
                    >
                      {webhookLog.event}
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        color: "#F9F9F9",
                        fontSize: "14px",
                        borderColor: "#606479",
                      }}
                    >
                      <DateAndTimeViewer dateAndTime={webhookLog.created_at} />
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        color: "#F9F9F9",
                        fontSize: "14px",
                        borderColor: "#606479",
                      }}
                    >
                      <Box
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 1,
                          padding: "4px 8px",
                          borderRadius: "100px",
                          backgroundColor: webhookLog.success
                            ? "rgba(101, 217, 132, 0.18)"
                            : "rgba(217, 101, 101, 0.18)",
                          border: webhookLog.success
                            ? "1px solid #65D984"
                            : "1px solid #D96565",
                          fontSize: "12px",
                          color: "#f9f9f9",
                        }}
                      >
                        {webhookLog.success
                          ? "Delivery Successful"
                          : "Delivery Failed"}
                      </Box>
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{ borderColor: "#606479", color: "#F9F9F9" }}
                    >
                      {webhookLog.response_status}
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{ borderColor: "#606479", color: "#F9F9F9" }}
                    >
                      --
                    </TableCell>
                    <TableCell align="right" sx={{ borderColor: "#606479" }}>
                      <Button
                        fullWidth
                        variant="outlined"
                        sx={{
                          textTransform: "none",
                          borderRadius: "10px",
                          border: "1px solid #6C65D9",
                          color: "#F9F9F9",
                        }}
                        onClick={() =>
                          setOpenPreviewModal({
                            open: true,
                            data: webhookLog.request_body,
                          })
                        }
                      >
                        Preview Payload
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <Modal
        open={addModalOpen.open}
        onClose={() =>
          setAddModalOpen({
            open: false,
            data: "",
          })
        }
        sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
      >
        <AddOrEditWebhookModal
          handleClose={() =>
            setAddModalOpen({
              open: false,
              data: "",
            })
          }
          setSnackValue={setSnackValue}
          projectId={projectId}
          eventsList={eventsList}
          handleRefreshCounter={getWebhook}
          editData={addModalOpen.data}
        />
      </Modal>
      <Modal
        open={openDeleteModal.open}
        onClose={() =>
          setOpenDeleteModal({
            open: false,
            data: {},
          })
        }
        sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
      >
        <DeleteModal
          name={openDeleteModal.data?.name}
          type="webhook"
          handleCloseDeleteModal={() =>
            setOpenDeleteModal({
              open: false,
              data: {},
            })
          }
          handleDelete={() => removeWebhook(openDeleteModal.data.id)}
        />
      </Modal>
      <Modal
        open={openPreviewModal.open}
        onClose={() =>
          setOpenPreviewModal({
            open: false,
            data: {},
          })
        }
        sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
      >
        <Box
          sx={{
            width: "600px",
            borderRadius: "20px",
            padding: "24px",
            bgcolor: "#1B1D28",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Typography sx={{ fontSize: "24px", fontWeight: "500" }}>
              Preview Payload
            </Typography>
            <IconButton
              onClick={() =>
                setOpenPreviewModal({
                  open: false,
                  data: {},
                })
              }
            >
              <X size={32} color="#F9F9F9" />
            </IconButton>
          </div>
          <Box
            sx={{ borderRadius: "8px", bgcolor: "#252A38", padding: "18px" }}
          >
            {JSON.stringify(openPreviewModal.data)}
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

export default WebhookItemsTable;
